import React, { useEffect, useState, useRef } from 'react';
import DiningRoom from './DiningRoom.js';
import LivingRoom from './LivingRoom';
import Hallway from './Hallway';
import Bedroom from './Bedroom';
import Performances from './Performances';
import BTS from './BTS';
import './HomePage.css';
import Papa from 'papaparse';
import Floorplan from './FloorPlan.js';

function HomePage() {
    const [room, setRoom] = useState('');
    const [roomData, setRoomData] = useState({});
    const [artistBios, setArtistBios] = useState(null);

    const justEnteredRoom = useRef(false);
    const hasUserNavigated = useRef(false);

    const plaqueRefs = useRef([]);
    const [activePlaqueIndex, setActivePlaqueIndex] = useState(0);

    // --- get the currently visible plaque or special indices ---
    const getCurrentPlaqueIndex = () => {
    const plaques = plaqueRefs.current || [];
    const viewportHeight = window.innerHeight;

    // 1️⃣ Floorplan: user just entered room and hasn't navigated
    if (justEnteredRoom.current) return -2;

    // 2️⃣ Collage: if collage exists and any part is visible in viewport
    const collage = document.querySelector('.collage');
    if (collage) {
        const rect = collage.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < viewportHeight) {
            return -1;
        }
    }

    // 3️⃣ Plaques: closest plaque to top
    let closestIndex = 0;
    let minDistance = Infinity;

    plaques.forEach((el, index) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top);
        if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
        }
    });

    return closestIndex;
};

const scrollToPlaque = (index) => {
    let el = null;

    if (index === -2) return; // floorplan has no scroll element
    else if (index === -1) el = document.querySelector('.collage');
    else el = plaqueRefs.current[index];

    if (!el) return;

    hasUserNavigated.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActivePlaqueIndex(index);

    setTimeout(() => {
        hasUserNavigated.current = false;
    }, 400);
};

const scrollUp = () => {
    const index = getCurrentPlaqueIndex();

    if (index === -2) return; // floorplan → nothing above
    else if (index === -1) scrollToPlaque(-2); // collage → floorplan
    else if (index === 0) scrollToPlaque(-1);  // first plaque → collage
    else scrollToPlaque(index - 1);
};

const scrollDown = () => {
    const plaques = plaqueRefs.current || [];
    const index = getCurrentPlaqueIndex();

    if (index === -2) scrollToPlaque(-1); // floorplan → collage
    else if (index === -1) scrollToPlaque(0); // collage → first plaque
    else if (index < plaques.length - 1) scrollToPlaque(index + 1);
};



    // --- scroll to top (floorplan) ---
    const scrollToTop = () => {
        hasUserNavigated.current = false;
        justEnteredRoom.current = true;
        setActivePlaqueIndex(0);

        document.querySelector('.nav')?.scrollIntoView({
            behavior: 'smooth'
        });

        setTimeout(() => {
            justEnteredRoom.current = false;
        }, 400);
    };

    // --- observe plaques ---
    useEffect(() => {
        setActivePlaqueIndex(-1);
        plaqueRefs.current = [];
        hasUserNavigated.current = false;

        if (!room) return;

        justEnteredRoom.current = true;

        requestAnimationFrame(() => {
            const collage = plaqueRefs.current[-1];
            if (collage) {
                collage.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }

            setTimeout(() => {
                justEnteredRoom.current = false;
            }, 400);
        });
    }, [room]);

    useEffect(() => {
        if (!room || !plaqueRefs.current.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (justEnteredRoom.current) return;
                if (!hasUserNavigated.current) return;

                const visibleEntries = entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

                if (visibleEntries.length > 0) {
                    const index = Object.keys(plaqueRefs.current).find(
                        key => plaqueRefs.current[key] === visibleEntries[0].target
                    );

                    if (index !== undefined) {
                        setActivePlaqueIndex(Number(index));
                    }
                }
            },
            {
                root: null,
                rootMargin: '0px 0px -40% 0px',
                threshold: [0.25, 0.5, 0.75],
            }
        );

        plaqueRefs.current.forEach(el => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [room, roomData]);

    // --- Load artist bios ---
    const loadArtists = () => {
        Papa.parse('bios.csv', {
            header: true,
            download: true,
            skipEmptyLines: true,
            complete: (results) => {
                const allData = results.data;

                const artistRegistry = allData.reduce((acc, row) => {
                    const id = row['artist id']?.trim();
                    if (!id) return acc;

                    const role = (row.role || '').trim();

                    if (!acc[id]) acc[id] = {};

                    acc[id][role] = {
                        id,
                        role,
                        firstName: row['first name'] || '',
                        lastName: row['last name'] || '',
                        bio: row.bio || '',
                        pieces: row.pieces || '',
                        instagram: row.instagram || '',
                        website: row.website || '',
                        email: row.email || '',
                        vimeo: row.vimeo || '',
                        spotify: row.spotify || '',
                    };

                    return acc;
                }, {});

                setArtistBios(artistRegistry);
            },
            error: (err) => console.error('CSV load error:', err),
        });
    };

    // --- Load plaques CSV ---
    const handleReadRemoteFile = () => {
        Papa.parse('plaques.csv', {
            header: true,
            download: true,
            skipEmptyLines: true,
            complete: (results) => {
                const allData = results.data;
                const groupedData = allData.reduce((acc, row) => {
                    const room = row.Room?.toLowerCase();
                    if (!room) return acc;
                    if (!acc[room]) acc[room] = [];
                    acc[room].push(row);
                    return acc;
                }, {});
                setRoomData(groupedData);
            },
            error: (err) => console.error('CSV load error:', err),
        });
    };

    const getArtistRow = (id) => {
        const rows = artistBios?.[id] || [];
        return rows.find(r => r.role === 'artist') || rows[0] || null;
    };

    useEffect(() => {
        loadArtists();
    }, []);

    useEffect(() => {
        if (artistBios) {
            handleReadRemoteFile();
        }
    }, [artistBios]);

    const displaySection = (room) => {
        if (!artistBios) return null;
        const data = roomData[room];

        switch (room) {
            case 'dining':
                return <DiningRoom data={data} artistBios={artistBios} getArtistRow={getArtistRow} plaqueRefs={plaqueRefs} />;
            case 'living':
                return <LivingRoom data={data} artistBios={artistBios} getArtistRow={getArtistRow} plaqueRefs={plaqueRefs} />;
            case 'hallway':
                return <Hallway data={data} artistBios={artistBios} getArtistRow={getArtistRow} plaqueRefs={plaqueRefs} />;
            case 'bedroom':
                return <Bedroom data={data} artistBios={artistBios} getArtistRow={getArtistRow} plaqueRefs={plaqueRefs} />;
            case 'performances':
                return <Performances data={data} artistBios={artistBios} plaqueRefs={plaqueRefs} />
            case 'bts':
                return <BTS />;
            default:
                return null;
        }
    };

    if (!artistBios) {
        return <div className="loading">Loading artists...</div>;
    }

    return (
        <div className="homePage">
            <div className="splash">
                <img
                    className="housesittingTiles"
                    alt="housesitting-tiles"
                    loading="lazy"
                    src="housesitting tiles.webp"
                />
                <div className="splashText">
                    <div>
                        On June 7th 2025, 27 artists and 12 performers all moved into a three bedroom apartment for Housesitting,
                        a one-night-exhibition of visual and sonic works that explored the transformation of a space into a home and
                        the destruction that can follow. What defines a home, and what makes one worth returning to?
                    </div>
                    <div>
                        The displayed works addressed art as a vehicle for familiarizing an unfamiliar space, examining the intertwinement
                        between object, memories, relationships, and experiences.
                    </div>
                </div>
                <div>
                    <img
                        className="downArrowButton"
                        alt="down arrow button"
                        loading="lazy"
                        src="button_1.webp"
                    />
                </div>
            </div>
            <div className="nav">
                <Floorplan roomData={roomData} activeRoom={room} setActiveRoom={setRoom} />
                {room && (
                    <div className="plaqueNav">
                        <button onClick={scrollUp}>↑</button>
                        <button onClick={scrollDown}>↓</button>
                        <button onClick={scrollToTop}>⇈</button>
                    </div>
                )}
                <div className="roomContent">{displaySection(room)}</div>
            </div>
            <footer className="siteFooter">
                <div className="footerText">
                    <i>website made and designed by eva, em, and sahana</i>
                </div>
                <img className="fingerprints" src={"thumbprints.png"} />
            </footer>
        </div>
    );
}

export default HomePage;
